import HomePageV2 from '@/app/modules/HomePageV2';
import { JobSearchHeader } from '@/app/modules/HomePageV2/sections/JobSearchHeader/JobSearchHeader';
import CmsModuleList from '@/cmsComponents/CmsModuleList';
import CMSContainer from '@/modules/CMSContainer';
import HomePageBanner from '@/modules/HomePageBanner';
import { faqSeoSchemaData } from '@/modules/HomePageV2/staticData/faqData';
import { DynamicPageData, DynamicPageType } from '@/types/cms';
import { getHomepageAbTestFlag, getJobSearchHeaderAbTestFlag } from '@/utils/getServerFeatureFlag';
import { useAnalytics } from '@/utils/useAnalytics';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { getDynamicPageData } from 'utils/getDynamicPageData';

interface HomePageProps extends DynamicPageData {
  newJobSearchHeader?: boolean;
  homePageV2?: boolean;
}

const HomePage = ({ data, pageData, homePageV2, newJobSearchHeader }: HomePageProps) => {
  const defaultTitle = 'Search over 2,000 Doctor Jobs in Australia and New Zealand.';
  const defaultDescription = 'Discover the right doctor job for you in Australia or New Zealand';
  const track = useAnalytics();

  useEffect(() => {
    track('Page View', {
      'Page Title': 'Homepage',
      'Live experiments': [
        {
          'A2P-3030-a-b-test-home-page': !!homePageV2,
        },
        {
          'A2P-3327-ab-test-jobsearchheader': !!newJobSearchHeader,
        },
      ],
    });
  }, [homePageV2, newJobSearchHeader, track]);

  return homePageV2 ? (
    <CMSContainer shouldUpdateFooterStyles>
      <NextSeo
        canonical={pageData.canonical_url}
        title={pageData.meta_title || defaultTitle}
        description={pageData.meta_description || defaultDescription}
        noindex={!pageData.index}
        nofollow={!pageData.index}
        openGraph={{
          title: pageData.meta_title || defaultTitle,
          description: pageData.meta_description || defaultDescription,
        }}
      />
      <FAQPageJsonLd mainEntity={faqSeoSchemaData} />
      <HomePageV2 />
    </CMSContainer>
  ) : (
    <CMSContainer>
      <NextSeo
        canonical={pageData.canonical_url}
        title={pageData.meta_title || defaultTitle}
        description={pageData.meta_description || defaultDescription}
        noindex={!pageData.index}
        nofollow={!pageData.index}
        openGraph={{
          title: pageData.meta_title || defaultTitle,
          description: pageData.meta_description || defaultDescription,
        }}
      />
      {newJobSearchHeader ? <JobSearchHeader /> : <HomePageBanner title={defaultTitle} />}
      <CmsModuleList modules={data} />
    </CMSContainer>
  );
};

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<HomePageProps>> {
  const homePageV2 = await getHomepageAbTestFlag(context);

  const {
    modules = [],
    pageData,
    error,
  } = await getDynamicPageData(DynamicPageType.Home_Page, context, undefined, homePageV2);

  const newJobSearchHeader = await getJobSearchHeaderAbTestFlag(context);

  if (error) {
    return { notFound: true };
  }

  if (pageData?.redirect_url) {
    return {
      redirect: {
        destination: pageData.redirect_url,
        permanent: true,
      },
    };
  }

  return {
    props: {
      homePageV2,
      data: modules,
      pageData,
      newJobSearchHeader,
    },
  };
}

export default HomePage;
